import { logger } from "@/utils/logger";
import { FileContent, QcHeader } from "@/types";
import {
  Masks,
  Paths,
  UON,
  headers,
  operator,
  setOtherFilters,
  upsertWithDataReturn,
  formatDateRecursively, deepClone, addMonth
} from "@/utils";
import { api } from "@/auth/api";
import { settings } from "@/settings";
import { useApi } from "@/requests/useApi";
import store from "@/store";
import { formatDate, formatDateLocale } from "@/utils/configuration/formatters-config";
import i18n from "@/i18n";
import moment from "moment";
import {codeConfirmation} from "../auth";
import { getBase64 } from "@/commons/gathering-process-order";
import { saveFile, updateFile } from "../offerService";
import Notify from 'quasar/src/plugins/Notify.js';;

export const userList = async (filter: any, offset? : any , limit?: any) => {
  const operators = [];

  const qc_header: QcHeader = {
    qc: {
      queryId: "user-lease-forge-user",
      offset: offset || 0,
      limit: limit || 1000,
      parameters: {
        ...filter,
      },
    },
  };

  const LEASE_FORGE_USER = "user_lease_forge_user";

  if (filter?.username) {
    operators.push(
      operator({
        val: filter.username,
        table: LEASE_FORGE_USER,
        dbField: "username",
      }).LIKE
    );
  }

  if (filter?.firstName) {
    operators.push(
      operator({
        val: filter.firstName,
        table: LEASE_FORGE_USER,
        dbField: "first_name",
      }).LIKE
    );
  }

  if (filter?.lastName) {
    operators.push(
      operator({
        val: filter.lastName,
        table: LEASE_FORGE_USER,
        dbField: "last_name",
      }).LIKE
    );
  }


  if (filter?.email) {
    operators.push(
      operator({
        val: filter.email,
        table: LEASE_FORGE_USER,
        dbField: "email",
      }).LIKE
    );
  }

  if (filter?.status) {
    let status = filter.status
    if(typeof status === 'string') {
      status = status === 'true'
    }
    operators.push(
        operator({
          val: status,
          table: LEASE_FORGE_USER,
          dbField: "enabled",
        }).EQUAL
    );
  }

  if (filter?.notExpired ) {
    let notExpired = filter.notExpired
    if(typeof notExpired === 'string') {
      notExpired = notExpired === 'true'
    }
    operators.push(
        operator({
          val: notExpired,
          table: LEASE_FORGE_USER,
          dbField: "account_not_expired",
        }).EQUAL
    );
  }

  if (filter?.notLocked) {
    let notLocked = filter.notLocked
    if(typeof notLocked === 'string') {
      notLocked = notLocked === 'true'
    }
    operators.push(
        operator({
          val: notLocked,
          table: LEASE_FORGE_USER,
          dbField: "account_not_locked",
        }).EQUAL
    );
  }

  if (filter?.credintialsNotExpired) {
    let credintialsNotExpired = filter.credintialsNotExpired
    if(typeof credintialsNotExpired === 'string') {
      credintialsNotExpired = credintialsNotExpired === 'true'
    }
    operators.push(
        operator({
          val: credintialsNotExpired,
          table: LEASE_FORGE_USER,
          dbField: "credentials_not_expired",
        }).EQUAL
    );
  }

  setOtherFilters(operators, qc_header);

  return await api().get(settings.api_query_url, {
    headers: headers({
      qc: JSON.stringify(qc_header.qc),
    }),
  });

}

export async function buildSelectedUser(user: any, payload: any) {


  if (user.userGroups.length === 0 || user.userGroups === undefined) {

    user.userGroups = [
      {
        businessData: null,
        daaq: "/",
        group: {
          objectType: "odm.user.securitygroup",
          resourceUid: "",
          systemUid: "odm-user",
        },
        objectType: "odm.user.usergroup",
        resourceUid: "",
        systemUid: "odm-user",
      }
    ]
  }
  // Audit fields are re-read from the API (not from the possibly stale list row)
  const auditRow = await findUserRowByUsername(user?.username ?? payload?.username);
  const whenCreated = auditRow?._when_created ?? payload?._when_created;
  const whenModified = auditRow?._when_modified ?? payload?._when_modified;
  const whoCreated = auditRow?._who_created ?? payload?._who_created;
  const modifierUsername = auditRow?._who_modified ?? payload?._who_modified;
  const modifierRow = await findUserRowByUsername(modifierUsername);
  const whoModified = modifierRow
    ? modifierRow.first_name + ' ' + modifierRow.last_name
    : (modifierUsername || '-');
  const EmptyUserEdit = {
    ...user,
    _when_created: whenCreated,
    _when_modified: whenModified,
    _who_created: whoCreated ?? "Anonymous",
    _who_modified: whoModified
  }
  return EmptyUserEdit
}

/**
 * Finds a user row by exact (case-insensitive) username.
 * userList filters with LIKE and drops an empty filter, so the result must never be read as data[0].
 */
export async function findUserRowByUsername(username: string | null | undefined) {
  const target = typeof username === "string" ? username.trim().toLowerCase() : "";
  if (!target) {
    return null;
  }
  try {
    const res = await userList({ username: username!.trim() });
    return (res?.data ?? []).find(
      (row: any) => row.username?.trim()?.toLowerCase() === target
    ) ?? null;
  } catch (e) {
    logger.warn("findUserRowByUsername failed", e);
    return null;
  }
}

export const getTeamFromUserId = async (resourceUid: string) => {
  const qc_header: QcHeader = {
    qc: {
      queryId: "team-of-user",
      offset: 0,
      limit: 100,
      parameters: { user_code: resourceUid }
    }
  };
  const { data } = await api().get(settings.api_query_url, {
    headers: headers({ qc: JSON.stringify(qc_header.qc) })
  });

  return data.map((el: { id: string, manager_code: string }) => {
    return {
      label: el.id,
      value: el.id,
      config: {
        manager_code: el.manager_code
      }

    }
  })
}

export const getTeamMembersByManagerCode = async (managerCode: string) => {
  const qc_header: QcHeader = {
    qc: {
      queryId: "user-team-members",
      offset: 0,
      limit: 100,
      parameters: { manager: managerCode }
    }
  };
  const { data } = await api().get(settings.api_query_url, {
    headers: headers({ qc: JSON.stringify(qc_header.qc) })
  });

  return data

}
export async function SaveParty(body: any) {


  body.birthDate = addHours(body.birthDate)
  const data = await upsertWithDataReturn(UON.SystemUid.odmParty, body, "person");
  return data
}
export async function SaveUser(body: any) {
  const clone = deepClone(body)
  delete clone._when_created
  delete clone._when_modified
  delete clone._who_created
  delete clone._who_modified
  if (!clone.passwordExpirationDate) {
    const date = addMonth(new Date(), 10)
    clone.passwordExpirationDate = formatDate(date, "/")
  }
  formatDateRecursively(clone, "DD/MM/YYYY", Masks.dateMask);

  try {
    if (
      store.state.userModule.isAvatarEdited &&
      store.state.userModule.selectedAvatar
    ) {
      const base64 = await getBase64(store.state.userModule.selectedAvatar);
      const fileContent: FileContent = {
        originalFileName: "User Avatar",
        format: "image/png",
        content: base64,
        relations: {
          items: [
            {
              qualifier: { rawValue: "User Avatar" },
              objectUON: {
                objectType: "odm.filemngt.filecontent",
                systemUid: UON.SystemUid.odmFileManagement,
              },
            },
          ],
        },
      };
      const image = await saveFile(fileContent);
      clone.avatarFileId = {
        resourceUid: image.resourceUid,
        objectType: "odm.filemngt.filedescriptor",
        systemUid: "odm-filemngt",
      };
    }
    return await upsertWithDataReturn(UON.SystemUid.odmUser, clone, "leaseforgeuser");
  } catch (error) {
    Notify.create({
      timeout: 3000,
      message: i18n.global.t("Upload Failed"),
      color: "negative",
    });
    throw error;
  }
}

export async function createUserGroup(userGroup: any, userResourceUid: string | undefined): Promise<any> {
  delete userGroup.resourceUid
  return await api().post(`${settings.api_url}/${UON.SystemUid.odmUser}/api/1/${UON.SystemUid.odmUser}/leaseforgeuser/${userResourceUid}/usergroup/`, userGroup)
}

export async function createUserAuthority(userDaaq: any, parentResourceUid: string | undefined): Promise<any> {
  return await api().post(`${settings.api_url}/${UON.SystemUid.odmUser}/api/1/${UON.SystemUid.odmUser}/leaseforgeuser/${parentResourceUid}/userdaaq/`, userDaaq)
}

export async function updateUserGroup(userGroup: any): Promise<any> {
  return await api().put(`${settings.api_url}/${UON.SystemUid.odmUser}/api/1/${UON.SystemUid.odmUser}/usergroup/${userGroup.resourceUid}/`, userGroup)
}

export async function getPartyRoleNetwork(payload: any) {
  const data = await api().get(`/${UON.SystemUid.odmParty}/api/1/${UON.SystemUid.odmParty}/networknode/${payload.networkNode}/partyrolenetwork/`);
  return data
}

export async function savePartyRoleNetwork(payload: any) {
  const body = payload.body
  const data = await api().post(`/${UON.SystemUid.odmParty}/api/1/${UON.SystemUid.odmParty}/networknode/${payload.networkNode}/partyrolenetwork/`, body);
  return data
}

export async function updatePartyRoleNetwork(payload: any) {
  const body = payload
  const data = await api().put(`/${UON.SystemUid.odmParty}/api/1/${UON.SystemUid.odmParty}/partyrolenetwork/${payload.resourceUid}/`, body);
  return data
}

export async function UpdateTeamMembers(user: any) {
  let team_id
  const managers = store.state.userModule.managers.filter((item: any) => item.manager_code === store.state.userModule.manager.id)[0]
  if(managers){
   team_id= managers.team_id
  }
  if(team_id){
      const { response } = await useApi(UON.SystemUid.odmUser, `${Paths.team}/${team_id}/`).get();
  const clone = response;

  if (clone.members.filter((item: any) => item.member.resourceUid === user.resourceUid).length === 0) {
    clone.members.push({
      "businessData": null,
      "daaq": "/",
      "objectType": "odm.user.teammember",
      "systemUid": "odm-user",
      "validity": {},
      "member": {
        "resourceUid": user.resourceUid,
        "objectType": "odm.user.leaseforgeuser",
        "systemUid": "odm-user",
      }
    })
  }


  clone.members.map((item: any) => {
    return {
      ...item.validity = {}

    }
  })
  clone.validity = {}
  const data = await api().put(
    `/${UON.SystemUid.odmUser}/api/1/${UON.SystemUid.odmUser}/${Paths.team}/${team_id}/`,
    clone
  );
  }

}

export const getManagerByTeam = async (managerCode: string) => {
  const qc_header: QcHeader = {
    qc: {
      queryId: "user-team-manager",
      offset: 0,
      limit: 100,
      parameters: { manager_code: managerCode }
    }
  };
  const { data } = await api().get(settings.api_query_url, {
    headers: headers({ qc: JSON.stringify(qc_header.qc) })
  });

  return data

}
export const addHours = (date: string) => {
  return moment(date).format('YYYY-MM-DD')
};



export async function getPerson(personId: string) {
  const { response } = await useApi(UON.SystemUid.odmParty, `${Paths.person}/${personId}/`).get();
  return response
}


export async function resetPasswordFromAdmin(username: string, newPassword: any) {
  const bodyRequest = {
    "newPassword": newPassword,
    "applicationName": "TWDDemo"
  }
  const { data } = await api().put(`/${UON.SystemUid.odmAuthentication}/api/1/${UON.SystemUid.odmAuthentication}/change-password/${username}/`, bodyRequest);
  if (!data.smsCodeValidated) {
    await codeConfirmation(data);
  }

  return data
}


export async function addUserAuthorities(userId: string) {
  const bodyRequest = {
    "daaqComponent": {
      "objectType": "odm.user.daaq",
      "systemUid": "odm-user",
      "resourceUid": "DEFAULT_DAAQ"
    }
  }
  const data = await api().post(`/${UON.SystemUid.odmUser}/api/1/${UON.SystemUid.odmUser}/leaseforgeuser/${userId}/userdaaq/`, bodyRequest);
  return data
}

export async function getUserLogs(userName: string) {
  const { response } = await useApi(UON.SystemUid.odmAuthentication, `${Paths.actions}/${userName}/`).get();
  return response
}
